import { useEffect } from 'react';
import { injectJsonLd, removeJsonLd } from '../lib/schemaBuilder';

interface VideoSchemaProps {
  name: string;
  description: string;
  thumbnailUrl: string;
  uploadDate: string;
  duration?: string;
  contentUrl: string;
  embedUrl?: string;
}

const BUSINESS_INFO = {
  name: 'Rockhound Auto',
  url: 'https://rockhoundauto.com',
  logo: 'https://static.readdy.ai/image/b3efdc45e89a2660551e85a9fdfadec5/a59b3ad9d14210b4ce617fff8e7523fb.png',
};

/**
 * ✅ Adds VideoObject structured data to the page for SEO.
 * Helps search engines understand and display video content in search results.
 * 
 * Uses shared injection helpers to prevent duplicate schemas on hot-reload.
 */
export function useVideoSchema(video: VideoSchemaProps | null) {
  useEffect(() => {
    if (!video) {
      // Clean up if video is removed
      removeJsonLd('video-schema');
      return;
    }

    // Derive embedUrl: use explicit value, or convert a watch URL to embed format
    const resolvedEmbedUrl = video.embedUrl
      ?? video.contentUrl.replace(
          /(?:https?:\/\/)?(?:www\.)?youtube\.com\/watch\?v=([^&]+)/,
          'https://www.youtube.com/embed/$1'
        );

    // Extract YouTube video ID to build multiple thumbnail resolutions
    const ytIdMatch = video.contentUrl.match(
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&?/]+)/
    );
    const ytId = ytIdMatch ? ytIdMatch[1] : null;

    // Google recommends at least 3 thumbnail sizes for maximum video rich result eligibility
    const thumbnailUrls: string[] = ytId
      ? [
          `https://img.youtube.com/vi/${ytId}/maxresdefault.jpg`,   // 1280×720
          `https://img.youtube.com/vi/${ytId}/hqdefault.jpg`,       // 480×360
          `https://img.youtube.com/vi/${ytId}/mqdefault.jpg`,       // 320×180
        ]
      : [video.thumbnailUrl];

    const schema: Record<string, unknown> = {
      '@context': 'https://schema.org',
      '@type': 'VideoObject',
      name: video.name,
      description: video.description,
      thumbnailUrl: thumbnailUrls,
      uploadDate: video.uploadDate,
      contentUrl: video.contentUrl,
      embedUrl: resolvedEmbedUrl,
      publisher: {
        '@type': 'Organization',
        name: BUSINESS_INFO.name,
        url: BUSINESS_INFO.url,
        logo: {
          '@type': 'ImageObject',
          url: BUSINESS_INFO.logo,
        },
      },
      regionsAllowed: ['US-OR', 'US-WA', 'US-CA', 'US-ID', 'US'],
      isFamilyFriendly: true,
      potentialAction: {
        '@type': 'WatchAction',
        target: {
          '@type': 'EntryPoint',
          urlTemplate: video.contentUrl,
          actionPlatform: [
            'http://schema.org/DesktopWebPlatform',
            'http://schema.org/MobileWebPlatform',
            'http://schema.org/IOSPlatform',
            'http://schema.org/AndroidPlatform',
          ],
        },
      },
    };

    if (video.duration) {
      // Convert MM:SS to ISO 8601 duration format (PT#M#S)
      const parts = video.duration.split(':');
      if (parts.length === 2) {
        const minutes = parseInt(parts[0], 10);
        const seconds = parseInt(parts[1], 10);
        schema.duration = `PT${minutes}M${seconds}S`;
      }
    }

    // ✅ Use shared injection helper — automatic duplicate prevention
    injectJsonLd('video-schema', schema);

    return () => {
      removeJsonLd('video-schema');
    };
  }, [video]);
}